/**
 * Bets by Sport analytics dashboard — shows betting activity broken down by sport, channel, and time.
 * Features: period presets, KPI scorecards, sport breakdown table, channel split, daily trend, top leagues.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';

import { useSessionContext } from '../auth/SessionProvider.tsx';
import {
  createBetsBySportApi,
  type BetsBySportResponse,
  type Period,
} from './bets-by-sport-api.ts';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount: number): string => {
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatCurrencyDecimal = (amount: number): string => {
  if (!Number.isFinite(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatNumber = (n: number): string => new Intl.NumberFormat('en-US').format(n);

const formatPercent = (n: number): string => {
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}%`;
};

const formatDate = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('es-PR', { day: 'numeric', month: 'short' });
};

// ─── Sport Config ────────────────────────────────────────────────────────────

interface SportConfig {
  readonly icon: string;
  readonly color: string;
  readonly bgColor: string;
}

const SPORT_CONFIGS: Record<string, SportConfig> = {
  Basketball: { icon: '🏀', color: 'text-foreground', bgColor: 'bg-muted' },
  'American Football': { icon: '🏈', color: 'text-foreground', bgColor: 'bg-muted' },
  Baseball: { icon: '⚾', color: 'text-foreground', bgColor: 'bg-muted' },
  Football: { icon: '⚽', color: 'text-foreground', bgColor: 'bg-muted' },
  'Ice Hockey': { icon: '🏒', color: 'text-foreground', bgColor: 'bg-muted' },
  Tennis: { icon: '🎾', color: 'text-foreground', bgColor: 'bg-muted' },
  'Table Tennis': { icon: '🏓', color: 'text-foreground', bgColor: 'bg-muted' },
  MMA: { icon: '🥊', color: 'text-foreground', bgColor: 'bg-muted' },
  Boxing: { icon: '🥊', color: 'text-foreground', bgColor: 'bg-muted' },
  Golf: { icon: '⛳', color: 'text-foreground', bgColor: 'bg-muted' },
};

const getSportConfig = (sport: string): SportConfig =>
  SPORT_CONFIGS[sport] ?? { icon: '🎯', color: 'text-foreground', bgColor: 'bg-muted' };

// ─── Components ──────────────────────────────────────────────────────────────

const KpiCard = ({
  label,
  value,
  subtitle,
  color = 'text-foreground',
}: {
  readonly label: string;
  readonly value: string;
  readonly subtitle?: string;
  readonly color?: string;
}): ReactElement => (
  <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className={`mt-1 font-display text-2xl font-bold ${color}`}>{value}</p>
    {subtitle !== undefined ? (
      <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
    ) : null}
  </div>
);

const PeriodPicker = ({
  period,
  onChange,
}: {
  readonly period: Period;
  readonly onChange: (p: Period) => void;
}): ReactElement => {
  const presets: { readonly label: string; readonly value: Period }[] = [
    { label: 'Hoy', value: 'today' },
    { label: '24h', value: '24h' },
    { label: '7 días', value: '7d' },
    { label: '30 días', value: '30d' },
    { label: '90 días', value: '90d' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {presets.map((preset) => (
        <button
          key={preset.value}
          type="button"
          onClick={() => onChange(preset.value)}
          className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
            period === preset.value
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-secondary-foreground'
          }`}
        >
          {preset.label}
        </button>
      ))}
    </div>
  );
};

const SportBreakdownTable = ({
  rows,
  totalHandle,
}: {
  readonly rows: BetsBySportResponse['bySport'];
  readonly totalHandle: number;
}): ReactElement => (
  <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
    <table className="w-full text-sm">
      <thead className="border-b border-border bg-muted/50">
        <tr>
          <th className="px-4 py-3 text-left font-semibold">Deporte</th>
          <th className="px-4 py-3 text-right font-semibold">Handle</th>
          <th className="px-4 py-3 text-right font-semibold">GGR</th>
          <th className="px-4 py-3 text-right font-semibold">Hold %</th>
          <th className="px-4 py-3 text-right font-semibold">Apuestas</th>
          <th className="px-4 py-3 text-right font-semibold">Avg Bet</th>
          <th className="px-4 py-3 text-right font-semibold">Jugadores</th>
          <th className="px-4 py-3 text-right font-semibold">% del Total</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {rows.map((row) => {
          const config = getSportConfig(row.SPORT);
          const holdPct = row.TOTAL_HANDLE > 0 ? (row.TOTAL_GGR / row.TOTAL_HANDLE) * 100 : 0;
          const pctOfTotal = totalHandle > 0 ? (row.TOTAL_HANDLE / totalHandle) * 100 : 0;

          return (
            <tr key={row.SPORT} className="hover:bg-muted/30 transition-colors">
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${config.bgColor}`}
                  >
                    {config.icon}
                  </span>
                  <span className="font-medium">{row.SPORT}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-right font-semibold">
                {formatCurrency(row.TOTAL_HANDLE)}
              </td>
              <td
                className={`px-4 py-3 text-right font-semibold ${row.TOTAL_GGR >= 0 ? 'text-foreground' : 'text-destructive'}`}
              >
                {formatCurrency(row.TOTAL_GGR)}
              </td>
              <td
                className={`px-4 py-3 text-right ${holdPct >= 0 ? 'text-foreground' : 'text-destructive'}`}
              >
                {formatPercent(holdPct)}
              </td>
              <td className="px-4 py-3 text-right">{formatNumber(row.BET_COUNT)}</td>
              <td className="px-4 py-3 text-right">{formatCurrencyDecimal(row.AVG_BET)}</td>
              <td className="px-4 py-3 text-right">{formatNumber(row.UNIQUE_PLAYERS)}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <div className="h-2 w-16 rounded-full bg-secondary">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${Math.min(pctOfTotal, 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground w-10 text-right">
                    {formatPercent(pctOfTotal)}
                  </span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const ChannelSplit = ({
  rows,
}: {
  readonly rows: BetsBySportResponse['byChannel'];
}): ReactElement => {
  const total = rows.reduce((s, r) => s + r.TOTAL_HANDLE, 0);

  const channelLabel = (id: string): string => {
    if (id === 'Mobile') return '📱 Mobile';
    if (id.includes('Retail')) return '🏪 Retail';
    return id;
  };

  const channelColor = (id: string): string => {
    if (id === 'Mobile') return 'bg-primary';
    return 'bg-secondary';
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <h3 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
        Canal
      </h3>
      <div className="space-y-4">
        {rows.map((row) => {
          const pct = total > 0 ? (row.TOTAL_HANDLE / total) * 100 : 0;
          return (
            <div key={row.CHANNEL_ID}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">{channelLabel(row.CHANNEL_ID)}</span>
                <span className="text-sm font-semibold">{formatCurrency(row.TOTAL_HANDLE)}</span>
              </div>
              <div className="h-3 w-full rounded-full bg-muted">
                <div
                  className={`h-3 rounded-full ${channelColor(row.CHANNEL_ID)} transition-all`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-muted-foreground">
                  {formatNumber(row.BET_COUNT)} apuestas
                </span>
                <span className="text-xs text-muted-foreground">{formatPercent(pct)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatusBreakdown = ({
  rows,
}: {
  readonly rows: BetsBySportResponse['byStatus'];
}): ReactElement => {
  const totalBets = rows.reduce((s, r) => s + r.BET_COUNT, 0);

  const statusConfig = (status: string): { label: string; color: string; icon: string } => {
    switch (status) {
      case 'LOST':
        return { label: 'Perdidas', color: 'text-foreground', icon: '✓' };
      case 'WON':
        return { label: 'Ganadas', color: 'text-destructive', icon: '×' };
      case 'CASHED_IN':
        return { label: 'Cash Out', color: 'text-muted-foreground', icon: '💰' };
      default:
        return { label: status, color: 'text-muted-foreground', icon: '•' };
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <h3 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
        Resultado
      </h3>
      <div className="space-y-3">
        {rows.map((row) => {
          const cfg = statusConfig(row.STATUS);
          const pct = totalBets > 0 ? (row.BET_COUNT / totalBets) * 100 : 0;
          return (
            <div key={row.STATUS} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm">{cfg.icon}</span>
                <span className={`text-sm font-medium ${cfg.color}`}>{cfg.label}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-semibold">{formatNumber(row.BET_COUNT)}</span>
                <span className="text-xs text-muted-foreground ml-2">({formatPercent(pct)})</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DailyTrendChart = ({
  rows,
}: {
  readonly rows: BetsBySportResponse['dailyTrend'];
}): ReactElement => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
        <h3 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">
          Tendencia Diaria
        </h3>
        <p className="text-center text-muted-foreground py-8">
          No hay datos de tendencia disponibles.
        </p>
      </div>
    );
  }

  const chartHeight = 140;
  const maxHandle = Math.max(...rows.map((r) => r.TOTAL_HANDLE), 1);
  const hoveredRow = hoveredIdx !== null ? rows[hoveredIdx] : null;

  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Tendencia Diaria
        </h3>
        {hoveredRow ? (
          <div className="flex items-center gap-4 text-xs">
            <span className="font-semibold">{formatDate(hoveredRow.DATE)}</span>
            <span>
              Handle: <strong>{formatCurrency(hoveredRow.TOTAL_HANDLE)}</strong>
            </span>
            <span className={hoveredRow.TOTAL_GGR >= 0 ? 'text-foreground' : 'text-destructive'}>
              GGR: <strong>{formatCurrency(hoveredRow.TOTAL_GGR)}</strong>
            </span>
            <span>
              Apuestas: <strong>{formatNumber(hoveredRow.BET_COUNT)}</strong>
            </span>
          </div>
        ) : null}
      </div>
      <div
        className="flex items-end gap-[2px]"
        style={{ height: `${String(chartHeight)}px` }}
        onMouseLeave={() => setHoveredIdx(null)}
      >
        {rows.map((row, i) => {
          const barHeight = Math.max(Math.round((row.TOTAL_HANDLE / maxHandle) * chartHeight), 3);
          const isPositiveGGR = row.TOTAL_GGR >= 0;
          const isHovered = hoveredIdx === i;

          return (
            <div
              key={row.DATE}
              className="flex-1 flex items-end cursor-pointer"
              style={{ height: `${String(chartHeight)}px` }}
              onMouseEnter={() => setHoveredIdx(i)}
            >
              <div
                className="w-full rounded-t"
                style={{
                  height: `${String(barHeight)}px`,
                  backgroundColor: isPositiveGGR
                    ? isHovered
                      ? '#059669'
                      : '#34d399'
                    : isHovered
                      ? '#dc2626'
                      : '#f87171',
                  opacity: hoveredIdx !== null && !isHovered ? 0.5 : 1,
                  transition: 'opacity 150ms, background-color 150ms',
                }}
              />
            </div>
          );
        })}
      </div>
      {/* X-axis labels */}
      <div className="flex justify-between mt-2">
        <span className="text-xs text-muted-foreground">
          {rows[0] ? formatDate(rows[0].DATE) : ''}
        </span>
        <span className="text-xs text-muted-foreground">
          {rows.length > 0 ? formatDate(rows[rows.length - 1]!.DATE) : ''}
        </span>
      </div>
      <div className="flex items-center gap-4 mt-3 justify-center">
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: '#34d399' }} />
          <span className="text-xs text-muted-foreground">GGR positivo</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full" style={{ backgroundColor: '#f87171' }} />
          <span className="text-xs text-muted-foreground">GGR negativo</span>
        </div>
      </div>
    </div>
  );
};

const TopLeaguesTable = ({
  rows,
}: {
  readonly rows: BetsBySportResponse['topLeagues'];
}): ReactElement => (
  <div className="rounded-xl border border-border bg-card shadow-sm">
    <div className="px-4 py-3 border-b border-border">
      <h3 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide">
        Top Ligas
      </h3>
    </div>
    <div className="divide-y divide-border">
      {rows.map((row, idx) => {
        const config = getSportConfig(row.SPORT);
        return (
          <div
            key={`${row.SPORT}-${row.LEAGUE}-${String(idx)}`}
            className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm shrink-0">{config.icon}</span>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{row.LEAGUE}</p>
                <p className="text-xs text-muted-foreground">{row.SPORT}</p>
              </div>
            </div>
            <div className="text-right shrink-0 ml-3">
              <p className="text-sm font-semibold">{formatCurrency(row.TOTAL_HANDLE)}</p>
              <p
                className={`text-xs ${row.TOTAL_GGR >= 0 ? 'text-foreground' : 'text-destructive'}`}
              >
                GGR: {formatCurrency(row.TOTAL_GGR)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

export const BetsBySportPage = (): ReactElement => {
  const session = useSessionContext();
  const api = useMemo(() => createBetsBySportApi(session.client), [session.client]);

  const [data, setData] = useState<BetsBySportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('7d');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getData(period);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error cargando datos');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [api, period, refreshKey]);

  const handlePeriodChange = useCallback((p: Period) => {
    setPeriod(p);
  }, []);

  if (loading && !data) {
    return (
      <section className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto" />
          <p className="mt-3 text-muted-foreground">Cargando datos de apuestas...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-destructive font-medium">Error: {error}</p>
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            className="mt-3 rounded-lg bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            Reintentar
          </button>
        </div>
      </section>
    );
  }

  if (!data) return <></>;

  return (
    <section aria-labelledby="bets-sport-heading" className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 id="bets-sport-heading" className="font-display text-2xl font-bold">
            Apuestas por Deporte
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {data.fromDate} — {data.toDate}
            {loading && <span className="ml-2 text-primary">Actualizando...</span>}
          </p>
        </div>
        <PeriodPicker period={period} onChange={handlePeriodChange} />
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <KpiCard
          label="Handle Total"
          value={formatCurrency(data.kpis.totalHandle)}
          subtitle="Volumen apostado"
        />
        <KpiCard
          label="GGR"
          value={formatCurrency(data.kpis.totalGGR)}
          color={data.kpis.totalGGR >= 0 ? 'text-foreground' : 'text-destructive'}
          subtitle="Ganancia bruta"
        />
        <KpiCard
          label="Hold %"
          value={formatPercent(data.kpis.holdPercent)}
          color={data.kpis.holdPercent >= 0 ? 'text-foreground' : 'text-destructive'}
          subtitle="Margen retenido"
        />
        <KpiCard
          label="Apuestas"
          value={formatNumber(data.kpis.totalBets)}
          subtitle="Total colocadas"
        />
        <KpiCard
          label="Avg Bet"
          value={formatCurrencyDecimal(data.kpis.avgBet)}
          subtitle="Promedio por apuesta"
        />
        <KpiCard
          label="Jugadores"
          value={formatNumber(data.kpis.totalPlayers)}
          subtitle="Únicos en período"
        />
      </div>

      {/* Daily Trend */}
      <DailyTrendChart rows={data.dailyTrend} />

      {/* Sport Breakdown */}
      <div>
        <h3 className="font-display text-lg font-semibold mb-3">Desglose por Deporte</h3>
        <SportBreakdownTable rows={data.bySport} totalHandle={data.kpis.totalHandle} />
      </div>

      {/* Bottom Grid: Channel + Status + Top Leagues */}
      <div className="grid gap-4 lg:grid-cols-3">
        <ChannelSplit rows={data.byChannel} />
        <StatusBreakdown rows={data.byStatus} />
        <TopLeaguesTable rows={data.topLeagues} />
      </div>
    </section>
  );
};
